import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../lib/http/axiosConfig";
import { totalDistance, twoOptOptimize } from "../lib/opt/twoOpt";

const initialState = {
  routeId: null,
  waypoints: [],
  optimizing: false,
  optimizeMeta: null,
  selectedCellIds: [],
  isRouteConfirmed: false,
  // 지도 셀과 미션 박스 순번 칩이 서로를 강조하도록 공유하는 호버 셀 id
  hoverCellId: null,
};

const normalizeWaypointList = (list) =>
  list.map((w) => ({
    id: w.id,
    seq: w.seq,
    gridCellId: w.gridCellId ?? w.gridCell ?? null,
    latitude: w.latitude ?? w.lat,
    longitude: w.longitude ?? w.lng,
    altitude: w.altitude ?? 0,
  }));

const toCoord = (w) => ({
  id: w.id,
  lat: +(w.latitude ?? w.lat),
  lng: +(w.longitude ?? w.longitude ?? w.lng),
  alt: +(w.altitude ?? w.alt ?? 0),
});
const fromCoord = (c, i) => ({
  id: c.id,
  seq: i + 1,
  latitude: c.lat,
  longitude: c.lng,
  altitude: c.alt,
});

// 1) 로컬 최적화

export const fetchWaypoints = createAsyncThunk(
  "route/fetchWaypoints",
  async ({ farmId }, { rejectWithValue }) => {
    if (!farmId) return rejectWithValue("No farmId");
    try {
      const { data } = await axios.get(`/api/drones/route/${farmId}/waypoints`);
      const list = Array.isArray(data.data) ? data.data : [];
      const routeId = list.length ? list[0].routeId : null;
      const waypoints = normalizeWaypointList(list);
      return { routeId, waypoints };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Fetch waypoints failed");
    }
  }
);

export const optimizeWaypointsLocal = createAsyncThunk(
  "route/optimizeLocal",
  async ({ fixStart = true, fixEnd = true } = {}, { getState }) => {
    const { waypoints } = getState().route;
    const coords = waypoints.map(toCoord);

    const t0 = performance.now();
    const before = totalDistance(coords);
    const opt = twoOptOptimize(coords, totalDistance, { fixStart, fixEnd });
    const after = totalDistance(opt);
    const t1 = performance.now();

    return {
      waypoints: opt.map(fromCoord),
      meta: {
        before,
        after,
        reduction: before
          ? (((before - after) / before) * 100).toFixed(1)
          : "0.0",
        elapsed: ((t1 - t0) / 1000).toFixed(2),
      },
    };
  }
);

// 2) 서버 저장만
export const saveWaypoints = createAsyncThunk(
  "route/saveWaypoints",
  async (_, { getState, rejectWithValue }) => {
    const { routeId, waypoints } = getState().route;

    if (!routeId) return rejectWithValue("No routeId");

    const body = {
      waypoints: waypoints.map((w, i) => ({
        waypointId: w.id,
        seq: i + 1,
      })),
    };

    const { data } = await axios.put(`/api/drones/${routeId}/optimize`, body);
    return data.data;
  }
);

// 3) 한 번에: 최적화 → 저장
export const optimizeAndSave = createAsyncThunk(
  "route/optimizeAndSave",
  async ({ fixStart = true, fixEnd = true } = {}, { dispatch, getState }) => {
    const { waypoints: prevWaypoints } = getState().route;

    try {
      const res = await dispatch(
        optimizeWaypointsLocal({ fixStart, fixEnd })
      ).unwrap();
      await dispatch(saveWaypoints()).unwrap();
      return res.meta;
    } catch (error) {
      console.error("optimizeAndSave 실패: ", error);
      dispatch(setWaypoints(prevWaypoints));
      return Promise.reject(error);
    }
  }
);

// 웨이포인트 추가 (POST)
export const addWaypoint = createAsyncThunk(
  "route/addWaypoint",
  async ({ routeId, newWp }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `/api/drones/${routeId}/waypoint`,
        newWp
      );
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Add waypoint failed");
    }
  }
);

// 웨이포인트 삭제 (DELETE)
export const removeWaypoint = createAsyncThunk(
  "route/removeWaypoint",
  async ({ routeId, id }, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(
        `/api/drones/${routeId}/waypoint/${id}`
      );
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Remove waypoint failed");
    }
  }
);

// 웨이포인트 수정 (PUT)
export const editWaypoint = createAsyncThunk(
  "route/editWaypoint",
  async ({ routeId, id, editWp }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `/api/drones/${routeId}/waypoint/${id}`,
        editWp
      );
      return data?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Remove waypoint failed");
    }
  }
);

export const updateRouteWaypoints = createAsyncThunk(
  "route/updateRouteWaypoints",
  async ({ routeId, waypoints }, { rejectWithValue }) => {
    try {
      const payload = {
        waypoints: waypoints.map((wp) => ({
          gridCellId: wp.gridCellId ?? wp.gridCell ?? wp.id,
          seq: wp.seq,
          altitude: wp.altitude ?? 0,
        })),
      };
      await axios.put(`/api/drones/${routeId}/waypoints/update`, payload);
      return true;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Update route waypoints failed"
      );
    }
  }
);
// 웨이포인트 초기화 (DELETE)
export const resetRoute = createAsyncThunk(
  "route/resetRoute",
  async ({ routeId }, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/drones/route/${routeId}`);
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Reset route failed");
    }
  }
);

const routeSlice = createSlice({
  name: "route",
  initialState,
  reducers: {
    setRouteId: (state, action) => {
      state.routeId = action.payload;
    },
    setWaypoints: (state, action) => {
      state.waypoints = action.payload;
    },
    setSelectedCellIds: (state, action) => {
      state.selectedCellIds = action.payload;
    },
    setRouteConfirmed: (state, action) => {
      state.isRouteConfirmed = action.payload;
    },
    setHoverCellId: (state, action) => {
      state.hoverCellId = action.payload ?? null;
    },
  },
  extraReducers: (b) => {

    b.addCase(fetchWaypoints.fulfilled, (s, action) => {
      const { payload } = action;
      s.routeId = payload.routeId ?? s.routeId;
      const allowSelectionOverwrite =
        action.meta?.arg?.allowSelectionOverwrite === true;
      if (s.selectedCellIds.length > 0 && !allowSelectionOverwrite) {
        return;
      }
      s.waypoints = payload.waypoints;
      // 백엔드에 confirm된 waypoints가 있을 때만 selectedCellIds 동기화
      // 빈 결과로 사용자가 방금 선택한 셀을 덮어쓰지 않도록 가드
      if (payload.waypoints.length > 0) {
        s.selectedCellIds = payload.waypoints
          .map((wp) => wp.gridCellId ?? wp.gridCell ?? wp.id)
          .filter(Boolean);
      }
      s.isRouteConfirmed = payload.waypoints.length > 0;
    });
    b.addCase(fetchWaypoints.rejected, (s) => {
      s.routeId = null;
      s.waypoints = [];
      // selectedCellIds 손대지 않음 - 사용자 선택 보존
      s.isRouteConfirmed = false;
    });


    // 웨이포인트 추가/수정/삭제
    b.addCase(addWaypoint.fulfilled, (s) => {
      // No-op: backend will stop returning full list
    });
    b.addCase(editWaypoint.fulfilled, (s) => {
      // No-op: backend will stop returning full list
    });
    b.addCase(removeWaypoint.fulfilled, (s) => {
      // No-op: backend will stop returning full list
    });

    // 웨이포인트 초기화
    b.addCase(resetRoute.fulfilled, (s) => {
      s.waypoints = [];
      s.selectedCellIds = [];
      s.isRouteConfirmed = false;
    });

    // 웨이포인트 최적화
    b.addCase(optimizeWaypointsLocal.pending, (s) => {
      s.optimizing = true;
    });
    b.addCase(optimizeWaypointsLocal.fulfilled, (s, { payload }) => {
      s.optimizing = false;
      s.waypoints = payload.waypoints;
      s.optimizeMeta = payload.meta;
    });
    b.addCase(optimizeWaypointsLocal.rejected, (s) => {
      s.optimizing = false;
    });

    b.addCase(optimizeAndSave.pending, (s) => {
      s.optimizing = true;
    });
    b.addCase(optimizeAndSave.fulfilled, (s, { payload }) => {
      s.optimizing = false;
      s.optimizeMeta = payload;
    });
    b.addCase(optimizeAndSave.rejected, (s) => {
      s.optimizing = false;
    });
  },
});

export const { setRouteId, setWaypoints, setSelectedCellIds, setRouteConfirmed, setHoverCellId } =
  routeSlice.actions;

/**
 * 선택한 셀 목록을 서버에 맞춘다.
 *
 * 선택은 농장 단위 공유 값이라, 한 사람이 바꾸면 접속 중인 모든 화면에 내려가야 한다.
 * 화면은 기다리지 않고 즉시 바꾸고(낙관적), 실패해도 되돌리지 않는다.
 * 다음 서버 전파가 오면 어차피 맞춰지고, 되돌리면 방금 누른 것이 튕겨 보인다.
 */
export const syncSelectedCellIds = createAsyncThunk(
  "route/syncSelectedCellIds",
  async (cellIds, { getState, dispatch }) => {
    dispatch(setSelectedCellIds(cellIds));
    const farmId = getState()?.farm?.farmId;
    if (!farmId) return cellIds;
    try {
      await axios.put(`/api/farms/${farmId}/cell-selection`, { cellIds });
    } catch (error) {
      console.warn("[CELLS] 선택 전송 실패", error);
    }
    return cellIds;
  }
);

export default routeSlice.reducer;
